import React, { useRef, useState } from 'react';
import { Camera, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { useApp } from '../context/AppContext';
import { fileUrl, needsDeliveryEvidence } from './OrderPayment';
import { compress } from '../lib/upload';
import { nativePhoto, haptic } from '../lib/native';

export const EvidenceGallery = ({ order, testId = 'delivery-evidence' }) => (
  <section data-testid={testId} className="space-y-3 border-t pt-4">
    <h2 className="text-base font-bold">Evidencia de entrega</h2>
    {!order.deliveryEvidence?.length && <p data-testid={`${testId}-empty`} className="text-xs text-gray-500">Sin fotos registradas</p>}
    {order.deliveryEvidence?.map((e) => <figure key={e.id} className="space-y-2">
      <a data-testid={`${testId}-open-${e.id}`} href={fileUrl(e.url)} target="_blank" rel="noreferrer"><img data-testid={`${testId}-photo-${e.id}`} src={fileUrl(e.url)} alt={`Evidencia del pedido ${order.id}`} className="w-full max-h-64 object-contain rounded-lg bg-gray-50" /></a>
      <figcaption data-testid={`${testId}-info-${e.id}`} className="text-xs text-gray-600 break-words">Pedido #{e.orderId} · {e.driverName} ({e.driverId})<br />{new Date(e.createdAt).toLocaleString('es-HN')} · {e.customerName}</figcaption>
    </figure>)}
  </section>
);

export const DeliveryEvidenceUploader = ({ order }) => {
  const { driver, saveDeliveryEvidence } = useApp();
  const camera = useRef(null);
  const gallery = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) return toast.error('La foto supera los 10 MB');
    setBusy(true);
    try { await saveDeliveryEvidence(order.id, driver.id, await compress(file)); haptic('success'); toast.success('Evidencia guardada en el pedido'); }
    catch { /* Context displays error; the order remains undelivered. */ } finally { setBusy(false); }
  };
  const choose = async (from) => {
    const file = await nativePhoto(from);
    if (file) return upload(file);
    (from === 'camera' ? camera : gallery).current?.click();
  };
  return <section data-testid="driver-evidence-uploader" className="space-y-2 border-t pt-3">
    <p data-testid="driver-evidence-requirement" className="text-sm font-bold">Foto de entrega {needsDeliveryEvidence(order) ? 'obligatoria' : '(opcional)'}</p>
    <div className="flex flex-wrap gap-2">
      <button data-testid="driver-evidence-camera" disabled={busy || order.deliveryEvidence?.length >= 5} onClick={() => choose('camera')} className="h-9 px-3 rounded-lg bg-red-50 text-[#EE3B36] text-xs font-bold flex items-center gap-1.5 disabled:opacity-50"><Camera size={15} />Tomar foto</button>
      <button data-testid="driver-evidence-gallery" disabled={busy || order.deliveryEvidence?.length >= 5} onClick={() => choose('photos')} className="h-9 px-3 border rounded-lg text-xs font-semibold flex items-center gap-1.5 disabled:opacity-50"><Upload size={15} />Subir imagen</button>
    </div>
    <input data-testid="driver-evidence-camera-input" ref={camera} type="file" accept="image/jpeg,image/png,image/webp" capture="environment" className="hidden" onChange={(e) => { upload(e.target.files[0]); e.target.value = ''; }} />
    <input data-testid="driver-evidence-file-input" ref={gallery} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => { upload(e.target.files[0]); e.target.value = ''; }} />
    {busy && <p data-testid="driver-evidence-uploading" role="status" className="text-xs text-gray-500">Guardando evidencia…</p>}
    {!!order.deliveryEvidence?.length && <p data-testid="driver-evidence-saved" className="text-xs text-emerald-700">{order.deliveryEvidence.length} foto(s) guardada(s) · Evidencia adjunta al pedido</p>}
  </section>;
};